import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getOrder } from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import { useWorkspace } from '../app/WorkspaceContext';
import { formatCurrency } from '../app/currency';
import { formatDateTime, formatDate } from '../app/date';

const statusLabels = {
  DRAFT: 'orders.draft',
  SENT: 'orders.sent',
  AWAITING_FORM: 'orders.awaiting_form',
  APPROVED: 'orders.approved',
  CONFIRMED: 'orders.confirmed',
  IN_PREPARATION: 'orders.in_preparation',
  DELIVERED: 'orders.delivered',
  REJECTED: 'orders.rejected',
  EXPIRED: 'orders.expired',
  CANCELLED: 'orders.cancelled',
};

const buildStatusHistory = (order) => {
  const history = order?.status_history;
  if (history && history.length > 0) {
    return [...history].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }
  const events = [];
  if (order?.created_at) {
    events.push({ status: 'DRAFT', previous_status: null, created_at: order.created_at });
  }
  if (order?.updated_at && order?.created_at !== order?.updated_at && order?.status !== 'DRAFT') {
    events.push({
      status: order.status,
      previous_status: 'DRAFT',
      created_at: order.updated_at,
    });
  }
  return events;
};

const OrderViewPage = () => {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (id) {
      loadOrder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await getOrder(id);
      setOrder(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !order) {
    return (
      <Box>
        <Typography>{translate('common.loading')}</Typography>
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  const statusHistory = buildStatusHistory(order);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {translate('orders.title')} – {order.number}{order.title ? ` – ${order.title}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate(`/orders/${id}/edit`)}>
            {translate('common.edit')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/orders')}>
            {translate('orders.back_to_list')}
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1">
          <strong>{translate('orders.number')}:</strong> {order.number}
        </Typography>
        {order.title && (
          <Typography variant="body1">
            <strong>{translate('orders.order_title')}:</strong> {order.title}
          </Typography>
        )}
        <Typography variant="body1">
          <strong>{translate('orders.status')}:</strong>{' '}
          <Chip label={translate(statusLabels[order.status] || 'orders.status')} size="small" />
        </Typography>
        <Typography variant="body1">
          <strong>{translate('orders.customer_id')}:</strong> {order.customer_name || order.customer_id || '—'}
        </Typography>
        {order.valid_until && (
          <Typography variant="body1">
            <strong>{translate('orders.valid_until')}:</strong> {formatDate(order.valid_until)}
          </Typography>
        )}
        {order.observations && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1">
              <strong>{translate('orders.observations') || 'Observações'}:</strong>
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {order.observations}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          {translate('orders.created_at')}: {formatDateTime(order.created_at) || '-'}
        </Typography>
        {order.status === 'AWAITING_FORM' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  const res = await getOrder(id);
                  const token = res?.data?.form_token;
                  const wsId = currentWorkspace?.id;
                  if (!token) {
                    setSnackbar({ open: true, message: translate('orders.save_first_for_link') || 'Salve o pedido para gerar o link.', severity: 'info' });
                  } else if (!wsId) {
                    setSnackbar({ open: true, message: translate('orders.select_workspace_for_link') || 'Selecione um espaço de trabalho.', severity: 'info' });
                  } else {
                    const url = `${window.location.origin}/formulario/${wsId}/${token}`;
                    await navigator.clipboard.writeText(url);
                    setSnackbar({ open: true, message: translate('orders.form_link_copied') || 'Link copiado!', severity: 'success' });
                  }
                } catch (err) {
                  setSnackbar({ open: true, message: err.message, severity: 'error' });
                }
              }}
            >
              {translate('orders.copy_form_link') || 'Copiar link do formulário'}
            </Button>
          </Box>
        )}
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        {translate('orders.status_history')}
      </Typography>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stepper orientation="vertical" activeStep={statusHistory.length}>
          {statusHistory.map((event, index) => (
            <Step key={index} completed>
              <StepLabel
                optional={
                  <Typography variant="body2" color="text.secondary" component="span">
                    {formatDateTime(event.created_at) || '-'}
                  </Typography>
                }
              >
                {event.previous_status
                  ? `${translate(statusLabels[event.previous_status] || 'orders.status')} → ${translate(statusLabels[event.status] || 'orders.status')}`
                  : translate(statusLabels[event.status] || 'orders.status')}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Typography variant="h6" gutterBottom>
        {translate('orders.lines')}
      </Typography>
      {(order.lines || []).map((line) => (
        <TableContainer key={line.id} component={Paper} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
            {translate('orders.line')} #{line.sequence} {line.description && `– ${line.description}`}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('orders.sku')}</TableCell>
                <TableCell>{translate('orders.quantity')}</TableCell>
                <TableCell>{translate('orders.unit_price')}</TableCell>
                <TableCell align="right">{translate('orders.final_price')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(line.items || []).map((it) => (
                <TableRow key={it.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {[it.product_name, it.sku_code].filter(Boolean).join(' – ') || it.sku_snapshot_id || '-'}
                    </Typography>
                    {it.attributes && Object.keys(it.attributes).length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {Object.entries(it.attributes).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{it.quantity}</TableCell>
                  <TableCell>{formatCurrency(it.unit_price)}</TableCell>
                  <TableCell align="right">{formatCurrency(it.quantity * it.unit_price)}</TableCell>
                </TableRow>
              ))}
              {line.final_price != null && (
                <TableRow>
                  <TableCell colSpan={3} align="right">
                    <strong>{translate('orders.final_price')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(line.final_price)}</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderViewPage;
